import { useState, useEffect } from 'react';
import ConsumerLayout from './ConsumerLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { rshd, notifications as notificationsApi, impact } from '../../utils/api';
import { useNavigate } from 'react-router-dom';
import { ShoppingBag, Heart, Bell, TrendingDown, Sparkles, Leaf, DollarSign, TreePine, Bot, HelpCircle } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { useLanguage } from '../../contexts/LanguageContext';
import DealShaqAgent from './DealShaqAgent';
import PersonalizedRecommendations from './PersonalizedRecommendations';
import ProductCard from './ProductCard';
// ConsumerWalkthrough now managed by ConsumerLayout - triggered via Tutorial nav button
import { useConsumerWalkthrough } from './ConsumerWalkthrough';
import { toast } from 'sonner';
import { HelpTooltip } from '../shared/HelpTooltip';

export default function ConsumerDashboard({ user, onLogout }) {
  const [featuredDeals, setFeaturedDeals] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [impactData, setImpactData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { t } = useLanguage();
  
  // Walkthrough - now triggered via Tutorial nav button in ConsumerLayout
  const { openWalkthrough } = useConsumerWalkthrough();

  useEffect(() => {
    loadData();
    loadImpactData();
    // Tutorial auto-show removed - now triggered via Tutorial button in nav
  }, []);

  const loadData = async () => {
    try {
      const [dealsRes, notifsRes] = await Promise.all([
        rshd.list(),
        notificationsApi.list(),
      ]);
      setFeaturedDeals(dealsRes.data.slice(0, 6));
      setNotifications(notifsRes.data.filter(n => !n.read).slice(0, 5));
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadImpactData = async () => {
    try {
      const response = await impact.consumerSummary();
      setImpactData(response.data);
    } catch (error) {
      console.log('Impact data not available:', error);
      setImpactData({ total_savings: 0, total_ghg_averted: 0, trees_equivalent: 0 });
    }
  };

  return (
    <ConsumerLayout user={user} onLogout={onLogout}>
      <div className="space-y-6">
        {/* Welcome Banner */}
        <div className="bg-gradient-to-r from-emerald-500 to-teal-600 rounded-2xl p-8 text-white shadow-lg">
          <h2 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
            {t('consumer.dashboard.welcome_back', 'Welcome back')}, {user.name}! 👋
          </h2>
          <p className="text-emerald-50 text-lg">
            {t('consumer.dashboard.subtitle', 'Local retailers are posting deals that match your favorites')}
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Button
              data-testid="browse-deals-btn"
              onClick={() => navigate('/consumer/browse')}
              className="bg-white text-emerald-600 hover:bg-emerald-50"
            >
              <ShoppingBag className="w-4 h-4 mr-2" />
              {t('consumer.dashboard.browse_deals', 'Browse Deals')}
            </Button>
            <Button
              data-testid="favorites-btn"
              onClick={() => navigate('/consumer/favorites')}
              variant="outline"
              className="border-white text-white hover:bg-white/10"
            >
              <Heart className="w-4 h-4 mr-2" />
              {t('consumer.dashboard.my_favorites', 'My Favorites')}
            </Button>
            <Button
              data-testid="tutorial-btn"
              onClick={openWalkthrough}
              variant="outline"
              className="border-white/50 text-white hover:bg-white/10"
            >
              <HelpCircle className="w-4 h-4 mr-2" />
              Tutorial
            </Button>
          </div>
        </div>

        {/* ConsumerWalkthrough moved to ConsumerLayout - triggered via Tutorial nav button */}

        {/* Quick Stats */}
        <div className="grid md:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200">
            <CardContent className="pt-6">
              <div className="flex items-center space-x-4">
                <div className="bg-emerald-500 p-3 rounded-lg">
                  <DollarSign className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-sm text-emerald-700">
                    {t('consumer.dashboard.lifetime_savings', 'Lifetime Savings')}
                  </p>
                  <p className="text-2xl font-bold text-emerald-700" data-testid="lifetime-savings">
                    ${impactData?.total_savings?.toFixed(2) || '0.00'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
            <CardContent className="pt-6">
              <div className="flex items-center space-x-4">
                <div className="bg-green-500 p-3 rounded-lg">
                  <Leaf className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-sm text-green-700">
                    {t('consumer.dashboard.co2_averted', 'CO₂ Averted')}
                  </p>
                  <p className="text-2xl font-bold text-green-700" data-testid="co2-averted">
                    {impactData?.total_ghg_averted?.toFixed(1) || '0'} kg
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-teal-50 to-teal-100 border-teal-200">
            <CardContent className="pt-6">
              <div className="flex items-center space-x-4">
                <div className="bg-teal-500 p-3 rounded-lg">
                  <TreePine className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-sm text-teal-700">
                    {t('consumer.dashboard.trees_equivalent', 'Trees Equivalent')}
                  </p>
                  <p className="text-2xl font-bold text-teal-700" data-testid="trees-equivalent">
                    {impactData?.trees_equivalent || 0}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card 
            className="cursor-pointer hover:shadow-md transition-shadow border-purple-200 bg-gradient-to-br from-purple-50 to-indigo-50"
            onClick={() => document.getElementById('food-assistant-section')?.scrollIntoView({ behavior: 'smooth' })}
          >
            <CardContent className="pt-6">
              <div className="flex items-center space-x-4">
                <div className="bg-gradient-to-br from-purple-500 to-indigo-600 p-3 rounded-lg">
                  <Bot className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-sm text-purple-700 font-medium">
                    DealShaq Shopping Assistant (DSA)
                  </p>
                  <p className="text-sm text-purple-600">Chat with me!</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* DealShaq Shopping Assistant (DSA) Section */}
        <div id="food-assistant-section">
          <DealShaqAgent 
            userName={user?.name}
            onAddToCart={(item) => {
              toast.success(`Added ${item.name} to cart`);
              // TODO: Integrate with actual cart functionality
            }}
          />
        </div>

        {/* Personalized Recommendations - AI-powered suggestions */}
        <PersonalizedRecommendations 
          onAddToCart={(item) => {
            toast.success(`Added ${item.name} to cart`);
            // TODO: Integrate with actual cart functionality
          }}
        />

        {/* Featured Deals */}
        <Card className="shadow-md">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingDown className="h-5 w-5 text-emerald-600" />
              {t('consumer.dashboard.featured_deals', 'Featured Deals')}
            </CardTitle>
            <CardDescription>
              {t('consumer.dashboard.hot_deals', 'Hot deals from your local retailers')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {[1, 2, 3].map(i => (
                  <div key={i} className="animate-pulse">
                    <div className="aspect-video bg-gray-200 rounded-t-lg" />
                    <div className="p-4 space-y-2">
                      <div className="h-4 bg-gray-200 rounded w-1/4" />
                      <div className="h-4 bg-gray-200 rounded w-3/4" />
                      <div className="h-4 bg-gray-200 rounded w-1/2" />
                    </div>
                  </div>
                ))}
              </div>
            ) : featuredDeals.length === 0 ? (
              <div className="text-center py-12">
                <div className="bg-gray-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <ShoppingBag className="w-10 h-10 text-gray-400" />
                </div>
                <p className="text-gray-500 mb-4">
                  {t('consumer.dashboard.no_deals', 'No deals available yet')}
                </p>
                <Button variant="outline" onClick={() => navigate('/consumer/browse')}>
                  Browse All Products
                </Button>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {featuredDeals.map((deal) => (
                  <div
                    key={deal.id}
                    onClick={() => navigate('/consumer/browse')}
                    className="cursor-pointer"
                  >
                    <ProductCard
                      item={deal}
                      variant="featured"
                      showStoreName={true}
                      showPLU={false}
                    />
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Notifications */}
        {notifications.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>{t('consumer.dashboard.recent_notifications', 'Recent Notifications')}</CardTitle>
              <CardDescription>
                {t('consumer.dashboard.deals_matching', 'New deals matching your favorites')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {notifications.map((notif) => (
                  <div
                    key={notif.id}
                    data-testid={`notification-${notif.id}`}
                    className="flex items-start space-x-3 p-3 bg-blue-50 rounded-lg"
                  >
                    <Bell className="w-5 h-5 text-blue-600 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm text-gray-900">{notif.message}</p>
                      <p className="text-xs text-gray-500 mt-1">
                        {new Date(notif.created_at).toLocaleString()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
              <Button
                onClick={() => navigate('/consumer/notifications')}
                variant="outline"
                className="w-full mt-4"
                data-testid="view-all-notifications-btn"
              >
                {t('consumer.dashboard.view_notifications', 'View All Notifications')}
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </ConsumerLayout>
  );
}
